import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import {StateController} from 'panel';

import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {reporter} from 'common/report/error-reporting';
import {sanitizeBookmarkParams as sanitizeFunnelsBookmarkParams} from 'common/report/funnels/util';
import {BadLayoutRequestErrors, ContentType, Dashboard} from 'common/report/dashboards/types';
import {ResponseError} from 'common/util/shared-client';

import {getContentsInGridLayout} from '../util/dashboards/grid-layout';
import {getCardIdFromContent} from '../util/dashboards/utils';
import {Store} from '../stores/store';
import {DashboardsMap} from '../stores/dashboards';
import {transformBookmarkToMultiMetricBookmark} from '../insights/models/utils';
import type {DashboardSelectorController} from './dashboard-selector-controller';
import {DashboardsModelClient, QuerySingleDashboardType} from './types';

export const INVALID_BOOKMARK_ERROR = `boards-invalid-bookmark`;
const INVALID_BOOKMARK = {params: {}, error: INVALID_BOOKMARK_ERROR, name: ``, description: ``};

export const setCardsFromDashboardLayout = (model: Dashboard) => {
  if (isEmpty(model.contents) || isEmpty(model.layout)) {
    model.cards = [];
    model.card_order = [];
    return;
  }

  const {
    [ContentType.Report]: bookmarkHash,
    [ContentType.ReportLink]: dashboardCardsHash,
    [ContentType.Text]: textCardsHash,
    [ContentType.Media]: mediaCardsHash,
    [ContentType.Dashboard]: nestedDashboardsHash,
    [ContentType.Benchmark]: benchmarkCardsHash,
  } = model.contents;

  const cardOrder = [];
  const cards = [];
  const contents = getContentsInGridLayout(model.layout);
  contents.forEach((content) => {
    const {contentId, contentType} = content;
    let card;
    const cardId = getCardIdFromContent(contentId, contentType);
    if (contentType === ContentType.Report) {
      card = getReportCard(bookmarkHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.ReportLink) {
      card = getReportLinkCard(dashboardCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Text) {
      card = getTextCard(textCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Media) {
      card = getMediaCard(mediaCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Dashboard) {
      card = getNestedDashboardCard(nestedDashboardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Benchmark) {
      card = getBenchmarkCard(benchmarkCardsHash, contentId, null, null, cardId);
    }

    cards.push(card);
    cardOrder.push(card.id);
  });

  model.cards = cards;
  model.card_order = cardOrder;

  function getBenchmarkCard(benchmarkHash, benchmarkId, cardWidth, cardHeight, cardId) {
    const benchmark = benchmarkHash[benchmarkId] || {};

    // to match `getReportCard` function
    if (benchmark.bookmark.type === `insights`) {
      benchmark.bookmark.params = transformBookmarkToMultiMetricBookmark(benchmark.bookmark.params);
    }
    return {
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      content_id: benchmarkId,
      benchmark_type: benchmark.benchmark_type,
      industry: benchmark.industry,
      bookmark: benchmark.bookmark,
      type: ContentType.Benchmark,
      ...(!isEmpty(benchmark.bookmark.metadata) ? {bookmark_metadata: benchmark.bookmark.metadata} : {}),
    };
  }

  function getReportLinkCard(dashboardCardsHash, reportLinkId, cardWidth, cardHeight, cardId) {
    const dashboardCard = dashboardCardsHash[reportLinkId] || {};
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {bookmark_metadata, chart_options, title} = dashboardCard;
    let {bookmark} = dashboardCard;
    if (!bookmark) {
      reporter.error(`Card ${cardId} doesn't have a valid bookmark`);
      bookmark = INVALID_BOOKMARK;
    }
    if (bookmark.type === `insights`) {
      bookmark.params = transformBookmarkToMultiMetricBookmark(bookmark.params);
    }

    return {
      id: cardId,
      title, // TODO use bookmark title when boards is GA
      description: bookmark?.description,
      width: cardWidth,
      height: cardHeight,
      content_id: reportLinkId,
      bookmark,
      // eslint-disable-next-line camelcase
      bookmark_metadata,
      // eslint-disable-next-line camelcase
      ...(!isEmpty(chart_options) ? {chart_options} : {}),
      linked_dashboard_card_id: reportLinkId,
      type: ContentType.ReportLink,
    };
  }

  function getTextCard(textCardsHash, textId, cardWidth, cardHeight, cardId) {
    const textCard = textCardsHash[textId] || {};
    return {
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      type: ContentType.Text,
      markdown: textCard.markdown,
      text_card_id: textId,
      content_id: textId,
    };
  }

  function getMediaCard(mediaCardsHash, mediaCardId, cardWidth, cardHeight, cardId) {
    const mediaCard = mediaCardsHash[mediaCardId] || {};
    return {
      // mediaCard.id is the same as mediaCardId
      ...mediaCard,
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      type: ContentType.Media,
      media_card_id: mediaCardId,
      content_id: mediaCardId,
    };
  }
};

function getNestedDashboardCard(nestedDashboardCardsHash, nestedDashboardCardId, cardWidth, cardHeight, cardId) {
  const nestedDashboardCard = nestedDashboardCardsHash[nestedDashboardCardId] || {};
  return {
    ...nestedDashboardCard,
    id: cardId,
    width: cardWidth,
    height: cardHeight,
    type: ContentType.Dashboard,
    dashboard_id: nestedDashboardCardId,
    content_id: nestedDashboardCardId,
  };
}

export function getReportCard(bookmarkHash, bookmarkId, cardWidth, cardHeight, cardId) {
  let bookmark = bookmarkHash[bookmarkId];
  // Only for opt-in/out purposes
  if (!bookmark) {
    reporter.error(`Card ${cardId} doesn't have a valid bookmark`);
    bookmark = INVALID_BOOKMARK;
  }

  const processedBookmark =
    bookmark.type === `insights`
      ? {...bookmark, params: transformBookmarkToMultiMetricBookmark(bookmark.params)}
      : bookmark;

  return {
    id: cardId,
    title: processedBookmark.name,
    description: processedBookmark.description,
    width: cardWidth,
    height: cardHeight,
    content_id: bookmarkId,
    bookmark: processedBookmark,
    type: ContentType.Report,
    ...(isEmpty(processedBookmark.metadata) ? {} : {bookmark_metadata: processedBookmark.metadata}),
  };
}

function sanitizeDashboardModel(dashboard) {
  function sanitizeDashboardCards(dashboardCards) {
    return dashboardCards.map((dashboardCard) => {
      const bookmark = dashboardCard.bookmark;
      if (bookmark?.type === BookmarkType.Funnels) {
        const sanitizedParams = sanitizeFunnelsBookmarkParams(bookmark.params);
        if (!isEmpty(sanitizedParams)) {
          const updatedBookmark = {
            ...bookmark,
            params: {
              ...bookmark.params,
              ...sanitizedParams,
            },
          };
          return {...dashboardCard, bookmark: updatedBookmark};
        }
      }
      return dashboardCard;
    });
  }

  const updatedParams: Partial<Dashboard> = {};
  if (dashboard.cards) {
    updatedParams.cards = sanitizeDashboardCards(dashboard.cards);
  }

  return {
    ...dashboard,
    ...updatedParams,
  };
}

type DashboardModelControllerState = {
  dashboard: Dashboard;
  isLoaded: boolean;
  error: ResponseError;
  modified: string;
};

export class DashboardModelController<
  CustomClientType extends DashboardsModelClient,
> extends StateController<DashboardModelControllerState> {
  parentController: DashboardSelectorController<CustomClientType>;
  client: CustomClientType;
  dashboardStore: Store<DashboardsMap>;
  _loadModelPromises: QuerySingleDashboardType;

  constructor({
    model,
    selectorController,
    dashboardsClient,
    dashboardStore,
  }: {
    model: Partial<Dashboard>;
    selectorController: DashboardSelectorController<CustomClientType>;
    dashboardsClient: CustomClientType;
    dashboardStore: Store<DashboardsMap>;
  }) {
    super(model as any);
    this.parentController = selectorController;
    this.client = dashboardsClient;

    // note: will be null if this is a public URL, stores don't work in unauthenticated environments
    this.dashboardStore = dashboardStore;

    this._update({dashboard: {...this.state.dashboard, ...model}});
  }

  get defaultState(): DashboardModelControllerState {
    return {
      dashboard: {
        id: null,
        title: null,
        description: null,
        creator_id: null,
        creator_name: null,
        creator_email: null,
        contents: null,
        card_order: null,
        cards: null,
        time_filter: null,
        filters: null,
        layout: null,
        // permissions
        can_view: false,
        can_update_basic: true,
        can_share: false,
        is_superadmin: false,
        allow_staff_override: false,

        is_favorited: false,
        pinned_date: null,
        num_active_public_links: 0,
        is_shared_with_project: false,
        parent_dashboard_id: null,
      },
      isLoaded: false,
      error: null,
      modified: null,
    };
  }

  updateModel(dashboardProps: Partial<Dashboard>) {
    const updatedDashboard = {...this.state.dashboard, ...dashboardProps};
    this._update({dashboard: updatedDashboard});
    this.parentController._updateCachedDashboard(this.state.dashboard.id, updatedDashboard);
  }

  getModel(): Dashboard {
    const dashboard = this.state.dashboard;
    const model: Dashboard = {...this.state.dashboard};
    if (!isEmpty(dashboard.time_filter) && Object.keys(dashboard.time_filter).length > 0) {
      const timeFilter = {...dashboard.time_filter};
      timeFilter.dateRange = new DateRange(timeFilter.dateRange);
      model.time_filter = timeFilter;
    } else {
      model.time_filter = {};
    }

    if (!dashboard.filters) {
      model.filters = [];
    }

    return model;
  }

  getLoaded() {
    return this.state.isLoaded;
  }

  // redundant method that is more consistent with out app verbiage
  isLoading() {
    return !this.state.isLoaded;
  }

  getError() {
    return this.state.error;
  }

  // TODO @shayan or @gonzalo: the networkOnly param is a workaround for the grid project. It is used just after we update a bookmark with the bookmark API, to refresh the base api cache
  async loadModel({networkOnly} = {networkOnly: false}) {
    this._update({isLoaded: false, error: null});

    const queryParams = {
      projectId: this.parentController.projectId,
      dashboardID: this.state.dashboard.id,
      workspaceId: this.parentController.workspaceId,
    };

    // prevent redundant network requests
    if (!this._loadModelPromises) {
      this._loadModelPromises = this.client.querySingle(queryParams, {clearCache: true});

      if (networkOnly && `network` in this._loadModelPromises) {
        this._loadModelPromises.cached = Promise.resolve(null);
      }
      // baseClient returns an object with both network and cache promises, while apiClient returns
      // a single promise. This will ensure both types of clients behave the same
      (Object.prototype.hasOwnProperty.call(this._loadModelPromises, `network`)
        ? Object.values(this._loadModelPromises)
        : [this._loadModelPromises]
      ).forEach((promise) =>
        promise
          .then((dashboard: Dashboard) => {
            if (dashboard) {
              const state: Partial<DashboardModelControllerState> = {
                dashboard: {...this.state.dashboard, ...sanitizeDashboardModel(dashboard)},
                isLoaded: true,
                error: null,
              };
              setCardsFromDashboardLayout(state.dashboard);
              this._update(state);

              // note: dashboardStore will be null if this is a public URL, stores don't work in unauthenticated environments
              if (this.dashboardStore) {
                const storeKey = this.state.dashboard.id.toString();
                // It's hacky but dashboardStore is manually updated here because Store fetching is hard to adapt to this model controller.
                // Why use a Store at all? To share/subscribe values with other Stores and start the ball rolling on a Store replacement for model controllers.
                // TODO: ideally DashboardModelController and DashboardSelectorController should be more thoroughly replaced by Stores or Stores v2 (ie. Resources)
                this.dashboardStore.update({[storeKey]: dashboard}, storeKey);
              }
            }
          })
          .catch((error) => {
            reporter.error(error);
            this._update({isLoaded: false, error});
          }),
      );
    }

    // fallback to network if cached value doesn't exist
    let cached;

    if (`cached` in this._loadModelPromises) {
      cached = await this._loadModelPromises.cached;
    }

    if (!cached && `network` in this._loadModelPromises) {
      await this._loadModelPromises.network;
    }

    Promise.all(Object.values(this._loadModelPromises))
      .catch((err) => {
        throw err;
      })
      .finally(() => (this._loadModelPromises = null));
  }

  async updateDashboard({fields = {}, updateModel = true} = {}) {
    const originalModel = this.getModel();
    try {
      if (updateModel) {
        // layout and contents not included
        const newProposedModel = {
          ...pick(fields, [`filters`, `time_filter`, `title`, `description`]),
        };

        this.updateModel(newProposedModel);
      }

      const results = await this.client.updateDashboardFields({
        projectId: this.parentController.projectId,
        workspaceId: this.parentController.workspaceId,
        dashboardID: this.state.dashboard.id,
        fields,
      });
      setCardsFromDashboardLayout(results);

      if (updateModel) {
        this.updateModel(results);
      }

      return results;
    } catch (error) {
      if (Object.values(BadLayoutRequestErrors).includes(error.response?.error?.type)) {
        const latestDashboard = error.response.error.dashboard;
        setCardsFromDashboardLayout(latestDashboard);
        this.updateModel(latestDashboard);
      } else {
        this.updateModel(originalModel);
      }
      throw error;
    }
  }

  // wrapper for base class StateController
  _update(props: Partial<DashboardModelControllerState>) {
    super._update(props);
  }
}
